import { ReactElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getPlanGeneralInfo,
  getCustomPlanGeneralInfo,
  isPlanCorporate,
} from '~utils/plans';
import { selectAuth } from '~store/slices/auth/index';
import { isAdminUser } from '~utils/user';
import { useAppSelector, useSubscription } from '~hooks/index';
import { getStringifyUrlQueryString } from '~utils/router';
import { openUrlInNewTab } from '~utils/commons';
import { PATHS } from '~constants/paths';
import { SCHEDULE_DEMO_URL } from '~constants/commons';
import { useHeaderActions } from '~contexts/HeaderActionsContext';

import { NewsMenuPlanInfoChipRoot } from './styles';

interface NewsMenuPlanInfoChipProps {
  name: string;
}

const defaultColor = '#00b0ff';

const NewsMenuPlanInfoChip = ({
  name,
}: NewsMenuPlanInfoChipProps): ReactElement => {
  const navigate = useNavigate();
  const subscription = useSubscription();

  const { user } = useAppSelector(selectAuth);

  const { menuNews } = useHeaderActions();

  const color = useMemo(() => {
    const currentPlanInfo = getPlanGeneralInfo(name);

    if (currentPlanInfo?.highlightColor) return currentPlanInfo.highlightColor;

    const currentCustomPlanInfo = getCustomPlanGeneralInfo(name);

    if (currentCustomPlanInfo?.highlightColor)
      return currentCustomPlanInfo.highlightColor;

    return defaultColor;
  }, [name]);

  const onClick = useMemo(() => {
    if (!isAdminUser(user)) return undefined;

    return () => {
      if (isPlanCorporate(subscription)) {
        openUrlInNewTab(SCHEDULE_DEMO_URL);
      } else {
        navigate(
          getStringifyUrlQueryString({
            url: PATHS.CONFIG.SUBSCRIPTIONS.BASE,
            query: {
              currentTab: 'plans-packages',
            },
          }),
        );
      }

      menuNews.handleToggleOpenMenu(false);
    };
  }, [menuNews, navigate, subscription, user]);

  return (
    <NewsMenuPlanInfoChipRoot
      label={name}
      variant="filled"
      customColor={color}
      onClick={onClick}
    />
  );
};

export default NewsMenuPlanInfoChip;
